import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaLock, FaSignOutAlt, FaTimes } from "react-icons/fa";
import { useScrollLock } from "../../Hooks/useScrollLock";

const CLOSE_DURATION_MS = 300;

interface MenuItem {
  to: string;
  label: string;
}

interface AdminMobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  items: MenuItem[];
  isActive: (to: string) => boolean;
  handleNav: (to: string) => (e: React.MouseEvent) => void;
  handleLogout: () => void;
  isGoogleUser: boolean;
  onChangePassword: () => void;
  userName?: string | null;
}

const AdminMobileSidebar = ({
  isOpen,
  onClose,
  items,
  isActive,
  handleNav,
  handleLogout,
  isGoogleUser,
  onChangePassword,
  userName,
}: AdminMobileSidebarProps) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);

  useScrollLock(isOpen);
  const navigate = useNavigate();
  const navigationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (navigationTimer.current) {
      clearTimeout(navigationTimer.current);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (isOpen) {
      setShouldRender(true);
      setIsVisible(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { if (!cancelled) setIsVisible(true); });
      });
    } else {
      setIsVisible(false);
      timer = setTimeout(() => setShouldRender(false), CLOSE_DURATION_MS);
    }
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [isOpen]);

  // Each page renders its own navbar, so changing route first would remove the sidebar mid-animation
  const handleItemClick = (to: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    onClose();
    navigationTimer.current = setTimeout(() => {
      if (isActive(to)) {
        handleNav(to)(e);
      } else {
        navigate(to);
      }
    }, CLOSE_DURATION_MS);
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <div className={`lg:hidden fixed inset-0 z-50 flex ${!isVisible ? "pointer-events-none" : ""}`}>
      <div className={`w-4/5 max-w-xs bg-white h-full overflow-y-auto shadow-xl flex flex-col transform transition-transform duration-300 ease-out ${isVisible ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between px-4 py-4 bg-black">
          <p className="text-sm font-bold text-white">Olá, {userName ?? "Admin"}!</p>
          <button onClick={onClose} className="text-white" aria-label="Fechar menu">
            <FaTimes />
          </button>
        </div>

        {/* The 1px overlap stops this line doubling the footer's when the list reaches it */}
        <div className="flex-1 -mb-px">
          <ul className="flex flex-col divide-y border-b text-sm text-gray-700">
            {items.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={handleItemClick(item.to)}
                  className={`w-full text-left block px-4 py-4 font-medium transition-colors ${isActive(item.to) ? "bg-primary-120 text-primary-600" : "text-black hover:bg-gray-50"}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t divide-y text-sm text-gray-700">
          {!isGoogleUser && (
            <button
              onClick={() => { onChangePassword(); onClose(); }}
              className="w-full text-left flex items-center gap-3 px-4 py-4 hover:bg-gray-50"
            >
              <FaLock className="text-gray-500" /> {"Alterar senha"}
            </button>
          )}
          <button
            onClick={() => { handleLogout(); onClose(); }}
            className="w-full text-left flex items-center gap-3 px-4 py-4 hover:bg-gray-50 text-gray-600"
          >
            <FaSignOutAlt className="text-gray-500" /> {"Sair"}
          </button>
        </div>
      </div>
      <div
        className={`flex-1 bg-black/40 transition-opacity duration-300 ${isVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />
    </div>
  );
};

export default AdminMobileSidebar;
